// CS Ticket

export interface CSTicket {
  id: string
  loadId: string
  displayInvoiceNumber: string
  orderTime: string | null
  clientId: string
  clientName: string
  sellerName: string
  prefix: string
  category: CSTicketCategory
  state: CSTicketState
  content: string
  priority: CSTicketPriority
  notes: string[]
  attachmentUrls: string[]
  source: CSTicketSource
  author: string
  authorUserId: string
  createTime: string
  lastUpdateTime: string
  firstStateUpdateTime: string | null
  comments: CSTicketComment[] | null
}

export type CSTicketState =
  | "PENDING_TO_DAY_CHECK"
  | "TO_DAY_IN_PROGRESS"
  | "PENDING_CLIENT_CHECK"
  | "CLIENT_IN_PROGRESS"
  | "COMPLETED"
  | "INCIDENT_DECLARED"

export type CSTicketCategory =
  | "WRONG_DELIVERY"
  | "NOT_RECEIVED"
  | "NOT_COLLECTED"
  | "DELIVERY_DELAYED"
  | "LOST_DAMAGED"
  | "OTHER"

export type CSTicketPriority = "LOW" | "HIGH"

export type CSTicketSource = "TO_DAY" | "CS_CENTER" | "CLIENT" | "SELLER"

export type CSTicketUserKind = "TO_DAY" | "CS_CENTER" | "CLIENT" | "SELLER"

// CS Ticket Comment

export interface CSTicketComment {
  id: string
  content: string
  source: CSTicketSource
  author: string
  authorUserId: string
  attachmentUrls: string[]
  createTime: string
  lastUpdateTime: string
}

// CS Ticket Author

export interface CSTicketAuthor {
  userId: string
  name: string
}

// CS Ticket Assignee

export interface CSTicketAssignee {
  id: string
  clientId: string
  sellerName: string
  userId: string
  userKind: CSTicketUserKind
  createTime: string
}

// Request types

interface CreateCSTicketRequestBase {
  displayInvoiceNumber: string
  sellerName?: string
  prefix?: string
  category: CSTicketCategory
  content: string
  priority: CSTicketPriority
  notes?: string[]
  attachmentUrls?: string[]
}

export type CreateCSTicketRequest = CreateCSTicketRequestBase &
  (
    | {
        source: "TO_DAY" | "CS_CENTER"
        author?: never
        authorUserId?: never
      }
    | {
        source: "CLIENT" | "SELLER"
        author: string
        authorUserId: string
      }
  )

export interface UpdateCSTicketRequest {
  state?: CSTicketState
  category?: CSTicketCategory
  priority?: CSTicketPriority
  content?: string
  notes?: string[]
  attachmentUrls?: string[]
  prefix?: string
}

export type CreateCSTicketCommentRequest = {
  content: string
  attachmentUrls?: string[]
} & (
  | { source: "TO_DAY" | "CS_CENTER"; author?: never; authorUserId?: never }
  | {
      source: "CLIENT" | "SELLER"
      author: string
      authorUserId: string
    }
)

export interface UpdateCSTicketCommentRequest {
  content?: string
  attachmentUrls?: string[]
}

export interface CreateCSTicketAssigneeRequest {
  clientId: string
  sellerName: string
  userId: string
  userKind: CSTicketUserKind
}

// Response types

export interface CSTicketStateCountMap {
  pendingToDayCheck: number
  toDayInProgress: number
  pendingClientCheck: number
  clientInProgress: number
  completed: number
  incidentDeclared: number
}

export interface CSTicketStatsResponse {
  totalCount: CSTicketStateCountMap
  over_1hCount: CSTicketStateCountMap
  over_48hCount: CSTicketStateCountMap
  over_72hCount: CSTicketStateCountMap
}

// Format functions

export function formatCSTicketState(state: CSTicketState): string {
  switch (state) {
    case "PENDING_TO_DAY_CHECK":
      return "To-Day 확인대기"
    case "TO_DAY_IN_PROGRESS":
      return "To-Day 확인중"
    case "PENDING_CLIENT_CHECK":
      return "고객사 확인대기"
    case "CLIENT_IN_PROGRESS":
      return "고객사 확인중"
    case "COMPLETED":
      return "처리완료"
    case "INCIDENT_DECLARED":
      return "사고확정"
    default:
      return state
  }
}

export function formatCSTicketCategory(category: CSTicketCategory): string {
  switch (category) {
    case "WRONG_DELIVERY":
      return "오배송"
    case "NOT_RECEIVED":
      return "미수령"
    case "NOT_COLLECTED":
      return "미회수"
    case "DELIVERY_DELAYED":
      return "배송지연"
    case "LOST_DAMAGED":
      return "분실/파손"
    case "OTHER":
      return "기타"
    default:
      return category
  }
}

export function formatCSTicketPriority(priority: CSTicketPriority): string {
  switch (priority) {
    case "LOW":
      return "일반"
    case "HIGH":
      return "긴급"
    default:
      return priority
  }
}

export function formatCSTicketSource(source: CSTicketSource): string {
  switch (source) {
    case "TO_DAY":
      return "To-Day"
    case "CS_CENTER":
      return "고객센터"
    case "CLIENT":
      return "고객사"
    case "SELLER":
      return "셀러"
    default:
      return source
  }
}

export function formatCSTicketUserKind(kind: CSTicketUserKind): string {
  switch (kind) {
    case "TO_DAY":
      return "To-Day"
    case "CS_CENTER":
      return "고객센터"
    case "CLIENT":
      return "고객사"
    case "SELLER":
      return "셀러"
    default:
      return kind
  }
}
